import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { webPageLd } from "../../lib/jsonld";
import JsonLd from "../JsonLd/JsonLd";
import Title from "../Title/Title";

const PAGE_TITLES: Record<string, string> = {
  "/": "Lunamalies | Lunar Anomaly Archive",
  "/catalog": "Catalog | Lunamalies",
  "/about": "About | Lunamalies",
  "/contact": "Contact | Lunamalies",
};

interface LayoutProps {
  title?: string;
  children: React.ReactNode;
  fullHeight?: boolean;
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  `transition-colors ${isActive ? "text-ray" : "hover:text-sunset"}`;

const Layout: React.FC<LayoutProps> = ({ title, children, fullHeight }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();
  const pageLd = location.pathname.startsWith("/events/")
    ? null
    : webPageLd(
        location.pathname,
        PAGE_TITLES[location.pathname] ?? "Lunamalies | Lunar Anomaly Archive",
      );

  return (
    <div
      className={
        fullHeight
          ? "h-full bg-alt-wht flex flex-col overflow-hidden"
          : "min-h-full bg-alt-wht flex flex-col"
      }
    >
      <Title title={title} />
      {pageLd && <JsonLd data={pageLd} />}
      <nav className="bg-alt-blk text-white py-4 sticky top-0 z-50 shrink-0">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <a
                href="https://earthboundmartian.com"
                className="flex items-center shrink-0"
                title="Earthbound Martian"
              >
                <img
                  src="/images/earthboundmartian-mascot-inverted-rgb-300px-w-72ppi.png"
                  alt="Earthbound Martian mascot"
                  className="h-10 sm:h-12 w-auto"
                />
                <img
                  src="/images/earthboundmartian-logotype-inverted-rgb-300px-w-72ppi.png"
                  alt="Earthbound Martian"
                  className="hidden sm:block h-10 sm:h-12 w-auto"
                />
              </a>
              <Link to="/" className="min-w-0">
                <div className="text-ray text-xl sm:text-2xl font-bold leading-tight tracking-tight">
                  Lunamalies
                </div>
                <div className="text-[11px] text-alt-wht/70 leading-none hidden sm:block">
                  Lunar anomaly archive
                </div>
              </Link>
            </div>

            <div className="hidden md:flex space-x-8 text-lg">
              <NavLink to="/" className={navClass} end>
                Map
              </NavLink>
              <NavLink to="/catalog" className={navClass}>
                Catalog
              </NavLink>
              <NavLink to="/about" className={navClass}>
                About
              </NavLink>
              <NavLink to="/contact" className={navClass}>
                Contact
              </NavLink>
            </div>

            <button
              className="md:hidden text-3xl leading-none"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Open menu"
            >
              ☰
            </button>
          </div>

          {isOpen && (
            <div className="md:hidden mt-4 flex flex-col space-y-3 text-center text-lg">
              <NavLink to="/" className={navClass} onClick={() => setIsOpen(false)} end>
                Map
              </NavLink>
              <NavLink
                to="/catalog"
                className={navClass}
                onClick={() => setIsOpen(false)}
              >
                Catalog
              </NavLink>
              <NavLink
                to="/about"
                className={navClass}
                onClick={() => setIsOpen(false)}
              >
                About
              </NavLink>
              <NavLink
                to="/contact"
                className={navClass}
                onClick={() => setIsOpen(false)}
              >
                Contact
              </NavLink>
            </div>
          )}
        </div>
      </nav>

      <main
        className={
          fullHeight
            ? "flex-1 min-h-0 h-0 flex flex-col"
            : "flex-1 flex flex-col"
        }
      >
        {children}
      </main>

      <footer className="bg-alt-blk text-white py-8 text-sm shrink-0">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <div>© 2026 Earthbound Martian, LLC • Richardson, TX</div>
            <div className="text-alt-wht/70 mt-1">
              Lunamalies is a project of Earthbound Martian
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
            <a
              href="mailto:lunamalies@earthboundmartian.com"
              className="text-ray hover:text-sunset transition-colors"
            >
              lunamalies@earthboundmartian.com
            </a>
            <a
              href="https://earthboundmartian.com"
              className="text-ray hover:text-sunset transition-colors"
            >
              earthboundmartian.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
